import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Button,
  CardMedia,
  makeStyles
} from "@material-ui/core";
import clsx from "clsx";
import { SelectField } from "@roc/ui";


const useStyles = makeStyles((theme) => ({
  img: {
    height: "150px",
    maxWidth: "100%",
    display: "block",
    width: "100%",
    objectFit: "cover",
  },
  statusBoxBase: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "5px",
    fontWeight: "bold",
    cursor: "default",
    transition: "background-color 0.3s, color 0.3s",
    "&:hover": {
      backgroundColor: "#9e9e9e",
      color: "#ffffff",
    }
  },
  selected: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  notSelected: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  selectReason: {
    border: "1px solid #ccc",
    borderRadius: "6px",
    padding: "10px 12px",
    fontSize: "16px",
    backgroundColor: "#fff",
    minHeight: "45px",
  },
  submitButtons: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    minHeight: "90px"
  },
  submitButton: {
    maxHeight: "45px",
    minWidth: "160px"
  },
  noImageContainer: {
    width: "100%",
    height: "150px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#d9d9d9"
  },
}));

const reasons = [
  "Inferior",
  "Superior",
  "Outdated",
  "Distant",
  "Off-market",
  "Zoning"
].map(op => ({
  label: op,
  value: op
}));

const propertyRows = [
  "address",
  "mls_number",
  "price",
  "proximity",
  "dom",
  "roomsBedroomsBathrooms",
  "sqft_price"
]

interface ComparableProperty {
  property_id: number;
  imageUrl: string;
  selected: boolean;
  address: string;
  mls_number: string;
  price: string;
  proximity: string;
  dom: string;
  roomsBedroomsBathrooms: string;
  sqft_price: string;
  reason: string;
  comparableNumber: number;
}

interface Props {
  comparables: ComparableProperty[];
  onReasonChange: (id: number, reason: string) => void;
  onApplyFeedback: () => void;
  onOmitFeedback: () => void;
}

const ComparablesFeedback: React.FC<Props> = ({
  comparables,
  onReasonChange,
  onApplyFeedback,
  onOmitFeedback
}) => {
  const classes = useStyles();
  const allReasonsProvided = comparables
    .filter(comp => !comp.selected)
    .every(comp => comp.reason && comp.reason.trim() !== "");

  return (
    <Box>
      <TableContainer>
        <Table style={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>
            <TableRow >
              {comparables.map((comp, index) => (
                <TableCell key={`image-${comp.property_id}`} align="center" style={{ paddingBottom: "32px" }}>
                  <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                    Comparable #{comp.comparableNumber}
                  </Typography>
                  {!comp.imageUrl ? (
                    <Box className={classes.noImageContainer} >
                      <p>Image not available</p>
                    </Box>
                  ) : (<CardMedia
                    component="img"
                    key={`${comp.property_id}-${comp.imageUrl}`}
                    className={classes.img}
                    image={comp.imageUrl}

                  />)}

                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              {comparables.map((comp) => (
                <TableCell key={`status-${comp.property_id}`} align="center">
                  <Box
                    component="span"
                    className={clsx(
                      classes.statusBoxBase,
                      comp.selected ? classes.selected : classes.notSelected
                    )}
                  >
                    {comp.selected ? "Selected" : "Not Selected"}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            {propertyRows.map((key) => (
              <TableRow key={key}>
                {comparables.map((comp) => (
                  <TableCell key={`${key}-${comp.property_id}`} align="center">
                    {(comp as any)[key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            <TableRow>
              {comparables.map((comp) => (
                <TableCell key={`reason-${comp.property_id}`} align="center">
                  <SelectField
                    disabled={comp.selected}
                    standaloneLabel={true}
                    value={comp.reason}
                    required={true}
                    options={reasons}
                    onChange={(val) => onReasonChange(comp.property_id, val)}
                    fullWidth
                    testId={`reason-${comp.property_id}`}
                    placeholder={"Select Reason"}
                    className={classes.selectReason}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.submitButtons}>
        <Button variant="outlined" color="primary" className={classes.submitButton} onClick={onOmitFeedback}>
          OMIT FEEDBACK
        </Button>
        <Button disabled={!allReasonsProvided} variant="contained" color="primary" className={classes.submitButton} onClick={onApplyFeedback}>
          APPLY FEEDBACK
        </Button>
      </Box>
    </Box>
  );
};

export default ComparablesFeedback;
